// In-App Debug-Konsole für iPhone-PWA.
//
// Fängt console.log/info/warn/error ab und zeigt die Logs in einem Overlay
// damit man OHNE Mac + USB-Kabel die Logs auf dem iPhone sehen kann.
// Speziell wichtig wenn die App als PWA vom Homescreen läuft — dann ist
// Safari Web Inspector frickelig, und die In-App-Variante funktioniert immer.
//
// Aktivierung:
//   - URL-Query: ?debug=1 (einmalig) → setzt localStorage-Flag, persistiert
//   - URL-Query: ?debug=0 → löscht das Flag
//   - LocalStorage-Flag 'adt_debug' === '1' → dauerhaft aktiv
//
// Bedienung:
//   - Schwebender 🐛-Button rechts oben (unter safe-area-inset-top)
//   - Tap öffnet/schließt das Panel
//   - 'Kopieren'-Button kopiert alle Logs in die Zwischenablage
//   - 'Clear'-Button leert die Log-Liste
//
// Bewusst NICHT als externe Library (eruda/vConsole): wir wollen offline-fähig
// sein ohne CDN-Abhängigkeit, und eine maßgeschneiderte kleine UI ohne
// iOS-Quirks (eruda positioniert sich unter iOS PWA manchmal falsch).

import { useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'adt_debug';
const MAX_LOGS = 500; // Ring-Buffer — ältere Einträge fallen raus

type Level = 'log' | 'info' | 'warn' | 'error';

interface LogEntry {
  id: number;
  ts: number;
  level: Level;
  text: string;
}

let nextId = 1;
const listeners = new Set<(entries: LogEntry[]) => void>();
let buffer: LogEntry[] = [];
let patched = false;

function serialize(args: unknown[]): string {
  return args
    .map((a) => {
      if (a === null) return 'null';
      if (a === undefined) return 'undefined';
      if (typeof a === 'string') return a;
      if (typeof a === 'number' || typeof a === 'boolean') return String(a);
      if (a instanceof Error) return `${a.name}: ${a.message}\n${a.stack || ''}`;
      try {
        return JSON.stringify(a, (_k, v) => {
          if (v instanceof Error) return `${v.name}: ${v.message}`;
          return v;
        }, 2);
      } catch {
        return String(a);
      }
    })
    .join(' ');
}

function pushEntry(level: Level, args: unknown[]): void {
  const entry: LogEntry = {
    id: nextId++,
    ts: Date.now(),
    level,
    text: serialize(args),
  };
  buffer = [...buffer, entry].slice(-MAX_LOGS);
  for (const l of listeners) l(buffer);
}

/**
 * Console-Hooks einmalig installieren. Rufen wir am allerfrühesten auf
 * damit auch Logs VOR dem React-Mount erfasst werden.
 */
export function installConsoleHooks(): void {
  if (patched) return;
  patched = true;
  const orig = {
    log: console.log.bind(console),
    info: console.info.bind(console),
    warn: console.warn.bind(console),
    error: console.error.bind(console),
  };
  console.log = (...args: unknown[]) => {
    orig.log(...args);
    pushEntry('log', args);
  };
  console.info = (...args: unknown[]) => {
    orig.info(...args);
    pushEntry('info', args);
  };
  console.warn = (...args: unknown[]) => {
    orig.warn(...args);
    pushEntry('warn', args);
  };
  console.error = (...args: unknown[]) => {
    orig.error(...args);
    pushEntry('error', args);
  };
  // Unhandled errors und Promise-Rejections zusätzlich erfassen —
  // die landen sonst oft nur in der native Console.
  window.addEventListener('error', (e) => {
    pushEntry('error', [
      `[unhandled] ${e.message}`,
      e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : '',
      e.error,
    ]);
  });
  window.addEventListener('unhandledrejection', (e) => {
    pushEntry('error', ['[unhandled rejection]', e.reason]);
  });
}

function debugActive(): boolean {
  try {
    const params = new URLSearchParams(location.search);
    if (params.get('debug') === '1') {
      localStorage.setItem(STORAGE_KEY, '1');
      return true;
    }
    if (params.get('debug') === '0') {
      localStorage.removeItem(STORAGE_KEY);
      return false;
    }
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function DebugConsole() {
  // Aktiv-Flag einmal beim Mount lesen. Wird nicht re-checked — wer das Flag
  // umdrehen will, muss neu laden. Hält den Render-Path sauber.
  const [active] = useState<boolean>(() => debugActive());
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<LogEntry[]>(buffer);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!active) return;
    const update = (b: LogEntry[]) => setEntries([...b]);
    listeners.add(update);
    return () => {
      listeners.delete(update);
    };
  }, [active]);

  // Auto-Scroll nach unten wenn neue Logs kommen und Panel offen ist
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [entries, open]);

  if (!active) return null;

  const errorCount = entries.filter((e) => e.level === 'error').length;
  const warnCount = entries.filter((e) => e.level === 'warn').length;

  const copyAll = async () => {
    const text = entries
      .map((e) => `[${new Date(e.ts).toISOString()}] [${e.level.toUpperCase()}] ${e.text}`)
      .join('\n');
    try {
      await navigator.clipboard.writeText(text);
      alert(`${entries.length} entries copied`);
    } catch (err) {
      alert('Copy failed: ' + (err instanceof Error ? err.message : String(err)));
    }
  };

  const clear = () => {
    buffer = [];
    setEntries([]);
  };

  // Dumps den kompletten CacheStorage-State ins Log. Wichtigster Knopf beim
  // Offline-Debugging: zeigt eindeutig welche PMTiles tatsächlich offline
  // verfügbar sind und wie groß die gecachten Responses sind.
  const inspectCache = async () => {
    if (typeof caches === 'undefined') {
      console.warn('[debug] CacheStorage nicht verfügbar');
      return;
    }
    try {
      const cacheNames = await caches.keys();
      console.info('[debug] Cache-Namen:', cacheNames);
      for (const name of cacheNames) {
        const cache = await caches.open(name);
        const keys = await cache.keys();
        console.info(`[debug] ── ${name}: ${keys.length} Einträge`);
        for (const req of keys) {
          let sizeInfo = '?';
          try {
            const resp = await cache.match(req);
            if (resp) {
              const cl = resp.headers.get('Content-Length');
              if (cl) {
                sizeInfo = `${(Number(cl) / 1024 / 1024).toFixed(2)} MB (Content-Length)`;
              } else {
                const blob = await resp.clone().blob();
                sizeInfo = `${(blob.size / 1024 / 1024).toFixed(2)} MB (blob)`;
              }
            }
          } catch (err) {
            sizeInfo = `lesen fehlgeschlagen: ${err instanceof Error ? err.message : String(err)}`;
          }
          console.info(`   ${req.url}  →  ${sizeInfo}`);
        }
      }
      const est =
        'storage' in navigator && navigator.storage.estimate
          ? await navigator.storage.estimate()
          : null;
      if (est) {
        console.info(
          `[debug] Quota: ${((est.usage ?? 0) / 1024 / 1024).toFixed(1)} / ${(
            (est.quota ?? 0) /
            1024 /
            1024 /
            1024
          ).toFixed(2)} GB`,
        );
      }
    } catch (err) {
      console.error('[debug] inspectCache failed', err);
    }
  };

  return (
    <>
      {/* Schwebender Toggle-Button — links oben UNTER safe-area-inset-top damit
          er nicht mit der Notch/Dynamic-Island oder den Karten-Controls kollidiert.
          Bewusst klein gehalten, halbtransparent, damit er nicht stört. */}
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed z-[60] rounded-full w-9 h-9 flex items-center justify-center text-sm font-mono shadow-lg bg-black/70 text-white border border-white/20 active:scale-95 transition"
        style={{
          right: '0.5rem',
          top: 'calc(env(safe-area-inset-top, 0px) + 0.5rem)',
          pointerEvents: 'auto',
        }}
        aria-label="Debug console"
      >
        {errorCount > 0 ? (
          <span className="text-red-400">🐛{errorCount}</span>
        ) : warnCount > 0 ? (
          <span className="text-amber-300">🐛{warnCount}</span>
        ) : (
          <span>🐛</span>
        )}
      </button>

      {open && (
        <div
          className="fixed inset-x-0 z-[59] bg-black/92 text-white flex flex-col border-t border-white/10"
          style={{
            top: 'calc(env(safe-area-inset-top, 0px) + 3rem)',
            bottom: 'calc(env(safe-area-inset-bottom, 0px) + 4rem)',
            // Tap-Blocker: Panel soll Karte unter sich nicht berühren lassen
            pointerEvents: 'auto',
          }}
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10 text-xs">
            <span className="font-semibold">Debug console</span>
            <span className="text-white/50">· {entries.length} entries</span>
            <div className="flex-1" />
            <button
              onClick={inspectCache}
              className="px-2 py-1 rounded bg-turbo-500/30 hover:bg-turbo-500/50 active:scale-95 transition"
              title="Shows what's in the PMTiles/Workbox cache"
            >
              Cache
            </button>
            <button
              onClick={copyAll}
              className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 active:scale-95 transition"
            >
              Copy
            </button>
            <button
              onClick={clear}
              className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 active:scale-95 transition"
            >
              Clear
            </button>
            <button
              onClick={() => setOpen(false)}
              className="px-2 py-1 rounded bg-white/10 hover:bg-white/20 active:scale-95 transition"
            >
              ✕
            </button>
          </div>
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto font-mono text-[11px] leading-tight"
          >
            {entries.length === 0 && (
              <div className="p-3 text-white/50">No log entries yet</div>
            )}
            {entries.map((e) => {
              const ts = new Date(e.ts);
              const tsStr = `${ts.getHours().toString().padStart(2, '0')}:${ts
                .getMinutes()
                .toString()
                .padStart(2, '0')}:${ts.getSeconds().toString().padStart(2, '0')}.${ts
                .getMilliseconds()
                .toString()
                .padStart(3, '0')}`;
              const colorClass =
                e.level === 'error'
                  ? 'text-red-300 bg-red-500/10'
                  : e.level === 'warn'
                  ? 'text-amber-200 bg-amber-500/10'
                  : e.level === 'info'
                  ? 'text-sky-200'
                  : 'text-white/90';
              return (
                <div
                  key={e.id}
                  className={`px-3 py-1 border-b border-white/5 whitespace-pre-wrap break-words ${colorClass}`}
                >
                  <span className="text-white/40 mr-2">{tsStr}</span>
                  <span className="text-white/60 mr-2 uppercase text-[9px]">{e.level}</span>
                  {e.text}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
}
